import { Stack } from "@mantine/core";
import { GraphQL, GraphQLPagedQueryData } from "@rpi/openapi-api";
import React from "react";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { RpaNumberStepperInput } from "../../components/inputFields/RpaNumberStepperInput";
import { RpaPriceEstimateTable } from "../../components/RpaPriceEstimateTable";
import { RpiBodyText } from "@rpi/openapi-core";
import { RpaTextInput } from "../../components/inputFields/RpaTextInput";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { shouldRetry } from "../../utils/query.util";
import { Button } from "@blurb/codex-react";
import { getBookPriceInfoUSD, MIN_PAGE_COUNT } from "../../utils/pricing.util";

interface PricingCalculatorProps {
  productId: number | undefined;
  customerProducts: GraphQLPagedQueryData<GraphQL.CustomerProductsQuery>[] | undefined;
  isOpened: boolean;
  onClose: () => void;
}

const formatProductName = (productName: string, customerSku: string) => {
  return `${productName} (${customerSku})`;
};

const parseProductName = (formattedName: string) => {
  return formattedName.split(" (")[0];
};

export const PricingCalculator: React.FC<PricingCalculatorProps> = ({
  productId,
  customerProducts,
  isOpened,
  onClose,
}) => {
  return (
    <RpaDrawer title="Price Calculator" opened={isOpened} onClose={onClose}>
      <PricingCalculatorFields
        productId={productId}
        customerProducts={customerProducts}
        isOpened={isOpened}
        onClose={onClose}
      />
    </RpaDrawer>
  );
};

export const PricingCalculatorFields: React.FC<PricingCalculatorProps> = ({
  productId,
  customerProducts,
  isOpened,
  onClose,
}) => {
  const currentCustomerProduct = customerProducts?.find((product) => product.id === productId);
  const [productName, setProductName] = React.useState<string | null>(null);
  const [quantity, setQuantity] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(MIN_PAGE_COUNT);

  const selectedProductName =
    productName !== null
      ? productName
      : formatProductName(currentCustomerProduct?.name || "", currentCustomerProduct?.customerSku || "");
  const customerProductNames =
    customerProducts?.map((product) => formatProductName(product.name!, product.customerSku!)) ?? [];

  const selectedProduct = customerProducts?.find((product) => product.name === parseProductName(selectedProductName));

  const { data: openapiProductData, isLoading } = GraphQL.useOpenApiProductWithPricingQuery(
    { id: selectedProduct?.openapiProductId || -1 },
    {
      enabled: !!selectedProduct?.openapiProductId && isOpened,
      retry: shouldRetry(onClose),
      cacheTime: 0,
      refetchOnMount: true,
    }
  );
  const priceInfo = getBookPriceInfoUSD(openapiProductData?.openapiProduct);

  const [showPricing, setShowPricing] = React.useState(false);

  const handleSubmit = () => {
    setShowPricing(true);
  };

  const handlePageCount = (value: number = MIN_PAGE_COUNT) => {
    // restrict to page increment
    const offset = value - priceInfo.minPageCount;
    const pageOffset = offset - (offset % priceInfo.pageIncrement);
    const pageValue = pageOffset + priceInfo.minPageCount;
    // clamp
    const clamped = Math.min(Math.max(pageValue, priceInfo.minPageCount), priceInfo.maxPageCount);

    setPageCount(clamped);
  };

  return (
    <Stack
      sx={(theme) => ({
        paddingLeft: `${theme.spacing.lg}px`,
        paddingRight: `${theme.spacing.lg}px`,
        marginBottom: `${theme.spacing.lg}px`,
      })}
    >
      <RpiBodyText>
        Please fill out the following information to calculate the estimated price (excluding shipping, taxes, and
        duties).
      </RpiBodyText>
      <RpaDropdown
        key={productId}
        data={customerProductNames}
        label="Product"
        value={selectedProductName}
        onChange={(value) => {
          setProductName(value);
          setShowPricing(false);
        }}
      />
      <RpaNumberStepperInput
        label="Page Count"
        description={`Please choose a number between ${priceInfo.minPageCount} and ${priceInfo.maxPageCount}, using increments of ${priceInfo.pageIncrement}.`}
        min={priceInfo.minPageCount}
        max={priceInfo.maxPageCount}
        step={priceInfo.pageIncrement}
        value={pageCount}
        onChange={handlePageCount}
        hideControls
      />
      <RpaTextInput
        label="Quantity"
        type="number"
        value={quantity}
        onChange={(e) => {
          const value = e.currentTarget.value;
          if (value === "" || /^\d+$/.test(value)) {
            const val = value === "" ? 0 : parseInt(value);
            setQuantity(val);
          }
        }}
      />
      <Button type="button" fullWidth={true} disabled={isLoading} onClick={handleSubmit}>
        Generate Estimate
      </Button>
      {showPricing && (
        <RpaPriceEstimateTable
          basePrice={priceInfo.usd?.basePrice ?? 0}
          additionalPagePrice={priceInfo.usd?.perPageCost ?? 0}
          minPageCount={priceInfo.minPageCount}
          quantity={quantity}
          pageCount={pageCount}
        />
      )}
    </Stack>
  );
};
